import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Save, X, Crown, Ban, RefreshCw, Trash2, ChevronDown, ChevronRight,
  Search, AlertTriangle, Fingerprint, ShieldAlert,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translate a raw watermark { gun_idx, field, delta } into a human-readable label
// using the gun-index map { pkg_id: [{name, category}, ...] }.
const SIGHT_LABEL = { "1x": "Iron / 1x", "2x": "2x", "3x": "3x", "4x": "4x" };
function describeWatermark(w, gunsForPack) {
  const g = (gunsForPack || [])[w.gun_idx] || {};
  const name = g.name || `gun #${w.gun_idx}`;
  const f = w.field || "";
  const sign = w.delta > 0 ? "+" : "";
  let axis = "";
  let sight = "";
  if (f.startsWith("h_")) axis = "H";
  else if (f.startsWith("v_")) axis = "V";
  const suffix = f.slice(2);
  if (suffix === "value") sight = "";
  else if (SIGHT_LABEL[suffix]) sight = ` ${SIGHT_LABEL[suffix]} sight`;
  return `${name} — ${axis}${sight} ${sign}${w.delta}`;
}

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [gunIndex, setGunIndex] = useState({});       // { pkg_id: [{name, category}, ...] }
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);   // only one expanded at a time
  const [draft, setDraft] = useState(null);             // per-user edit state for the expanded row
  const [saving, setSaving] = useState(false);
  const [showLeakTool, setShowLeakTool] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, p, g] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/packages`),
        axios.get(`${API}/admin/gun-index`),
      ]);
      setUsers(u.data);
      setPackages(p.data);
      setGunIndex(g.data || {});
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpand = (u) => {
    if (expandedId === u.id) {
      setExpandedId(null);
      setDraft(null);
      return;
    }
    setExpandedId(u.id);
    setDraft({
      is_admin: !!u.is_admin,
      granted_packages: [...(u.granted_packages || [])],
    });
  };

  const togglePackage = (pkgId) => {
    setDraft((d) => {
      const has = d.granted_packages.includes(pkgId);
      return {
        ...d,
        granted_packages: has
          ? d.granted_packages.filter((x) => x !== pkgId)
          : [...d.granted_packages, pkgId],
      };
    });
  };

  const saveDraft = async (u) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/users/${u.id}`, {
        is_admin: draft.is_admin,
        granted_packages: draft.granted_packages,
      });
      toast.success("User updated");
      await load();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetIp = async (id) => {
    if (!window.confirm("Reset IP locks for this user? Next request will rebind.")) return;
    try {
      await axios.post(`${API}/admin/users/${id}/reset-ip`, null);
      toast.success("IP locks cleared");
      await load();
    } catch {
      toast.error("Reset failed");
    }
  };

  const ban = async (id) => {
    if (!window.confirm("Ban this user? Their Discord ID + IP will be blocked from re-signing up.")) return;
    try {
      await axios.post(`${API}/admin/users/${id}/ban`, null);
      toast.success("User banned");
      await load();
    } catch {
      toast.error("Ban failed");
    }
  };

  const unban = async (id) => {
    try {
      await axios.post(`${API}/admin/users/${id}/unban`, null);
      toast.success("User unbanned");
      await load();
    } catch {
      toast.error("Unban failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this user? They can register again unless banned.")) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      toast.success("User deleted");
      setExpandedId(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = users.filter((u) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      (u.username || "").toLowerCase().includes(q) ||
      (u.global_name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.discord_id || "").includes(q)
    );
  });

  if (loading) return <div className="text-white/40 text-sm">Loading users…</div>;

  return (
    <div data-testid="users-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display font-bold uppercase text-2xl">Registered users</h2>
          <p className="text-white/55 text-sm mt-1">
            {users.length} total · click a user&apos;s name to expand their dossier.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowLeakTool(true)}
            className="text-xs uppercase tracking-widest border border-[var(--rv-red)]/40 rounded-md px-3 py-2 inline-flex items-center gap-2 text-[var(--rv-red)] hover:bg-[var(--rv-red)]/10 transition"
            data-testid="open-leak-identifier"
          >
            <Fingerprint size={14} /> Identify leaker
          </button>
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, email, Discord ID…"
            className="bg-black/40 border-white/10 text-white max-w-xs"
            data-testid="users-filter-input"
          />
        </div>
      </div>

      <div className="mt-6 space-y-2">
        {filtered.map((u) => (
          <UserRow
            key={u.id}
            user={u}
            packages={packages}
            gunIndex={gunIndex}
            expanded={expandedId === u.id}
            draft={expandedId === u.id ? draft : null}
            saving={saving && expandedId === u.id}
            onToggle={() => toggleExpand(u)}
            onTogglePackage={togglePackage}
            onToggleAdmin={(v) => setDraft((d) => ({ ...d, is_admin: v }))}
            onSave={() => saveDraft(u)}
            onResetIp={() => resetIp(u.id)}
            onBan={() => ban(u.id)}
            onUnban={() => unban(u.id)}
            onDelete={() => remove(u.id)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="text-white/40 text-sm py-10 text-center">No users found.</div>
        )}
      </div>

      {showLeakTool && (
        <LeakIdentifierModal
          packages={packages}
          onClose={() => setShowLeakTool(false)}
        />
      )}
    </div>
  );
}

/* =================================================================== */
/* User row — expandable                                               */
/* =================================================================== */
function UserRow({
  user: u, packages, gunIndex, expanded, draft, saving,
  onToggle, onTogglePackage, onToggleAdmin, onSave,
  onResetIp, onBan, onUnban, onDelete,
}) {
  const ipCount = (u.ip_history || []).length;
  const deviceCount = (u.device_ids || []).length;
  const deviceWarn = deviceCount >= 3;

  return (
    <div className={`rounded-xl border ${expanded ? "border-[var(--rv-red)]/50 bg-[var(--rv-red)]/[0.04]" : "border-white/10 bg-black/30"} transition`}
         data-testid={`user-row-${u.id}`}>
      {/* Header row — click to expand */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/[0.03] transition"
        data-testid={`user-toggle-${u.id}`}
      >
        <div className="shrink-0">
          {expanded ? <ChevronDown size={18} className="text-[var(--rv-red)]" /> : <ChevronRight size={18} className="text-white/40" />}
        </div>
        {u.avatar ? (
          <img
            src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png?size=64`}
            alt=""
            className="h-11 w-11 rounded-full shrink-0"
          />
        ) : (
          <div className="h-11 w-11 rounded-full bg-[var(--rv-red)]/30 flex items-center justify-center text-sm font-bold shrink-0">
            {(u.global_name || u.username || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display uppercase text-lg flex items-center gap-2 truncate">
            {u.global_name || u.username || "(unnamed)"}
            {u.is_admin && <Crown size={14} style={{ color: "var(--rv-gold)" }} />}
            {u.is_banned && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">Banned</span>
            )}
          </div>
          <div className="text-xs text-white/45 truncate">{u.discord_id} · {u.email || "no email"}</div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-widest">
          <span className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-white/65">
            {(u.granted_packages || []).length} packs
          </span>
          <span className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-white/65">
            {ipCount} ips
          </span>
          <span className={`rounded-full border px-2.5 py-1 ${deviceWarn ? "border-red-500/50 text-red-300" : "border-white/10 text-white/65"}`}>
            {deviceCount}/3 devs
          </span>
        </div>
      </button>

      {expanded && draft && (
        <div className="border-t border-white/10 p-5 space-y-6" data-testid={`user-expand-${u.id}`}>
          {/* Dossier */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Last known IP" value={u.last_known_ip || "—"} />
            <Stat label="IP locations (tracked)" value={`${ipCount}`} />
            <Stat label="Device fingerprints" value={`${deviceCount} / 3`} warn={deviceWarn} />
            <Stat label="Joined" value={(u.created_at || "").slice(0, 10) || "—"} />
          </div>

          {(u.ip_history || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/55 mb-1.5">IP history</div>
              <div className="flex gap-1.5 flex-wrap">
                {u.ip_history.map((ip) => (
                  <span key={ip} className="text-xs font-mono px-2 py-1 rounded bg-black/40 border border-white/10 text-white/75">{ip}</span>
                ))}
              </div>
            </div>
          )}

          {/* Watermarks (leak fingerprint) */}
          {u.watermarks && Object.keys(u.watermarks).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/55 mb-2 inline-flex items-center gap-1.5">
                <Fingerprint size={11} /> Leak fingerprint (per-pack ±1 offsets)
              </div>
              <div className="space-y-2">
                {Object.entries(u.watermarks).map(([pid, wms]) => {
                  const pkg = packages.find((p) => p.id === pid);
                  const gunsForPack = gunIndex?.[pid] || [];
                  return (
                    <div key={pid} className="text-sm rounded-md border border-white/10 bg-black/30 px-3 py-2">
                      <div className="font-display uppercase tracking-tight text-white text-xs mb-1">{pkg?.name || pid}</div>
                      <ul className="space-y-0.5">
                        {(wms || []).map((w) => (
                          <li key={`${w.gun_idx}-${w.field}-${w.delta}`} className="text-white/75 font-mono text-xs">
                            • {describeWatermark(w, gunsForPack)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={draft.is_admin}
              onChange={(e) => onToggleAdmin(e.target.checked)}
              className="accent-[var(--rv-red)] h-4 w-4"
              data-testid={`user-isadmin-toggle-${u.id}`}
            />
            <span className="text-sm text-white/85 inline-flex items-center gap-2">
              <Crown size={14} style={{ color: "var(--rv-gold)" }} /> Make this user an admin
            </span>
          </label>

          {/* Granted packs */}
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60 mb-3">Granted packages</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {packages.map((p) => {
                const checked = draft.granted_packages.includes(p.id);
                return (
                  <label
                    key={p.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                      checked ? "border-[var(--rv-red)]/50 bg-[var(--rv-red)]/10" : "border-white/10 hover:border-white/20"
                    }`}
                    data-testid={`user-pkg-toggle-${u.id}-${p.id}`}
                  >
                    <input type="checkbox" checked={checked} onChange={() => onTogglePackage(p.id)} className="accent-[var(--rv-red)] h-4 w-4" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase tracking-tight text-sm truncate">{p.name}</div>
                      <div className="text-[10px] text-white/45 mt-0.5">${p.price.toFixed(2)} · {p.tier}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Action bar */}
          <div className="flex gap-2 flex-wrap pt-2 border-t border-white/10">
            <button
              onClick={onSave}
              disabled={saving}
              className="btn-primary"
              data-testid={`user-save-btn-${u.id}`}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
            </button>
            <button
              onClick={onResetIp}
              className="text-xs uppercase tracking-widest border border-white/15 rounded px-3 py-2 hover:border-yellow-500/60 hover:text-yellow-300 transition inline-flex items-center gap-2"
              data-testid={`user-reset-ip-${u.id}`}
            >
              <RefreshCw size={13} /> Reset device lock
            </button>
            {u.is_banned ? (
              <button
                onClick={onUnban}
                className="text-xs uppercase tracking-widest border border-white/15 rounded px-3 py-2 hover:border-emerald-400 hover:text-emerald-300 transition"
                data-testid={`user-unban-${u.id}`}
              >
                Unban
              </button>
            ) : (
              <button
                onClick={onBan}
                className="text-xs uppercase tracking-widest border border-white/15 rounded px-3 py-2 hover:border-red-400 hover:text-red-300 transition inline-flex items-center gap-2"
                data-testid={`user-ban-${u.id}`}
              >
                <Ban size={13} /> Ban
              </button>
            )}
            <button
              onClick={onDelete}
              className="text-xs uppercase tracking-widest border border-white/15 rounded px-3 py-2 text-white/55 hover:text-[var(--rv-red)] hover:border-[var(--rv-red)] transition inline-flex items-center gap-2 ml-auto"
              data-testid={`user-delete-${u.id}`}
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className={`rounded-md border px-3 py-2.5 ${warn ? "border-yellow-500/45 text-yellow-200" : "border-white/10 text-white/80"}`}>
      <div className="text-[10px] uppercase tracking-widest text-white/55">{label}</div>
      <div className="text-sm font-mono truncate mt-1">{value}</div>
    </div>
  );
}

/* =================================================================== */
/* Leak Identifier Modal                                               */
/* =================================================================== */
function LeakIdentifierModal({ packages, onClose }) {
  const [pkgId, setPkgId] = useState("");
  const [content, setContent] = useState(null);     // {guns: [...]}
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState({});       // { "gun_idx:field": leaked }
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  // VIP packs don't have watermarks — only show standard packs
  const standardPackages = packages.filter((p) => p.tier !== "vip");

  const loadContent = async (id) => {
    if (!id) return;
    setLoading(true);
    setContent(null);
    setSamples({});
    setResult(null);
    try {
      const r = await axios.get(`${API}/admin/packages/${id}/content`);
      setContent(r.data.content || { guns: [] });
    } catch {
      toast.error("Failed to load pack content");
    } finally {
      setLoading(false);
    }
  };

  const onPickPack = (e) => {
    const v = e.target.value;
    setPkgId(v);
    loadContent(v);
  };

  const setSample = (gunIdx, field, value) => {
    const key = `${gunIdx}:${field}`;
    setSamples((s) => ({ ...s, [key]: value }));
  };

  const scan = async () => {
    const payload = Object.entries(samples)
      .filter(([, v]) => (v || "").trim() !== "")
      .map(([key, leaked]) => {
        const [gunIdx, field] = key.split(":");
        return { gun_idx: parseInt(gunIdx, 10), field, leaked: leaked.trim() };
      });
    if (payload.length === 0) {
      toast.error("Enter at least one leaked H/V value");
      return;
    }
    setScanning(true);
    try {
      const r = await axios.post(`${API}/admin/identify-leaker`, {
        pkg_id: pkgId,
        samples: payload,
      });
      setResult(r.data);
    } catch {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const guns = (content?.guns || []).filter((g) => g.enabled);

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-w-3xl w-full bg-[#0a0a0a] border border-white/10 rounded-xl my-8" data-testid="leak-identifier-modal">
        {/* header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Fingerprint size={20} style={{ color: "var(--rv-red)" }} />
            <div>
              <h3 className="font-display font-black uppercase text-xl">Identify Leaker</h3>
              <p className="text-xs text-white/55 mt-0.5">Enter the leaked H/V values you saw and find out who they belong to.</p>
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/70 border border-white/15 flex items-center justify-center hover:bg-[var(--rv-red)] transition" data-testid="leak-tool-close">
            <X size={18} />
          </button>
        </div>

        {/* pack selector */}
        <div className="p-5 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/55">Step 1 · Pick the leaked pack</label>
            <select
              value={pkgId}
              onChange={onPickPack}
              className="mt-2 w-full bg-black/40 border border-white/15 rounded-md px-3 py-2.5 text-sm text-white"
              data-testid="leak-pack-select"
            >
              <option value="">— select —</option>
              {standardPackages.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* gun samples */}
          {loading && <div className="text-white/40 text-sm">Loading guns…</div>}
          {!loading && content && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-2">
                Step 2 · Enter the leaked H/V you saw
                <span className="text-white/40 normal-case tracking-normal text-xs">(only fill the ones you have — more samples = sharper match)</span>
              </label>
              {guns.length === 0 ? (
                <div className="mt-3 text-white/45 text-sm">No enabled guns on this pack.</div>
              ) : (
                <div className="mt-3 max-h-[40vh] overflow-y-auto space-y-2 pr-1">
                  {guns.map((g) => {
                    const idx = content.guns.indexOf(g);
                    const isAr = g.category === "ar";
                    return (
                      <div key={`${idx}-${g.name}`} className="rounded-md border border-white/10 p-3">
                        <div className="font-display uppercase tracking-tight text-sm mb-2">
                          {g.name} <span className="text-white/40 text-xs">#{idx} · {g.category}</span>
                        </div>
                        {isAr ? (
                          <div className="grid grid-cols-4 gap-2">
                            {["1x", "2x", "3x", "4x"].map((s) => (
                              <div key={s}>
                                <div className="text-[9px] uppercase tracking-widest text-white/45 text-center mb-1">{s.toUpperCase()}</div>
                                <Input
                                  placeholder={`H (${g[`h_${s}`] || "—"})`}
                                  value={samples[`${idx}:h_${s}`] || ""}
                                  onChange={(e) => setSample(idx, `h_${s}`, e.target.value)}
                                  className="bg-black/40 border-white/10 text-white text-xs h-8 px-2 mb-1"
                                />
                                <Input
                                  placeholder={`V (${g[`v_${s}`] || "—"})`}
                                  value={samples[`${idx}:v_${s}`] || ""}
                                  onChange={(e) => setSample(idx, `v_${s}`, e.target.value)}
                                  className="bg-black/40 border-white/10 text-white text-xs h-8 px-2"
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder={`Leaked H (orig ${g.h_value || g.value || "—"})`}
                              value={samples[`${idx}:h_value`] || ""}
                              onChange={(e) => setSample(idx, "h_value", e.target.value)}
                              className="bg-black/40 border-white/10 text-white text-sm h-9"
                            />
                            <Input
                              placeholder={`Leaked V (orig ${g.v_value || "—"})`}
                              value={samples[`${idx}:v_value`] || ""}
                              onChange={(e) => setSample(idx, "v_value", e.target.value)}
                              className="bg-black/40 border-white/10 text-white text-sm h-9"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* scan button */}
          {content && (
            <button onClick={scan} disabled={scanning} className="btn-primary w-full justify-center" data-testid="leak-scan-btn">
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <><Search size={16} /> Identify leaker</>}
            </button>
          )}

          {/* results */}
          {result && (
            <div className="border-t border-white/10 pt-5 space-y-3" data-testid="leak-result">
              <div className="text-xs text-white/55">
                Scanned <span className="text-white">{result.scanned_user_count}</span> user(s) with this pack ·
                found <span className="text-[var(--rv-red)] font-bold">{result.match_count}</span> match(es)
              </div>

              {result.match_count === 0 && (
                <div className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-3 flex items-start gap-3">
                  <AlertTriangle size={16} className="text-yellow-300 mt-0.5 shrink-0" />
                  <div className="text-sm text-yellow-200/90">
                    No match. Possible reasons: the leaked values are from a non-watermarked gun (i.e. no ±1 was assigned to it for any user — try entering values from a different gun), the pack content has changed since the leak, or the leaker has multiple of your packs.
                  </div>
                </div>
              )}

              {result.match_count > 1 && (
                <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-3 flex items-start gap-3">
                  <ShieldAlert size={16} className="text-orange-300 mt-0.5 shrink-0" />
                  <div className="text-sm text-orange-200/90">
                    Multiple users match these samples. Add more leaked H/V values to narrow it down to a single user.
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {result.matches.map((m) => (
                  <div key={m.id} className="rounded-md border border-[var(--rv-red)]/40 bg-[var(--rv-red)]/5 p-4">
                    <div className="flex items-center gap-3">
                      {m.avatar ? (
                        <img src={`https://cdn.discordapp.com/avatars/${m.discord_id}/${m.avatar}.png?size=64`} alt="" className="h-10 w-10 rounded-full" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-[var(--rv-red)]/30 flex items-center justify-center text-sm font-bold">
                          {(m.global_name || m.username || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-display uppercase text-base flex items-center gap-2">
                          {m.global_name || m.username}
                          {m.is_admin && <Crown size={12} style={{ color: "var(--rv-gold)" }} />}
                          {m.is_banned && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">Banned</span>}
                        </div>
                        <div className="text-xs text-white/55 truncate">{m.discord_id} · {m.email || "no email"} · last IP {m.last_known_ip || "—"}</div>
                      </div>
                    </div>
                    {m.watermarks_for_pack?.length > 0 && (
                      <div className="mt-3 text-xs text-white/75 font-mono space-y-0.5">
                        <div className="text-[10px] uppercase tracking-widest text-white/55 font-sans mb-1">Their fingerprint:</div>
                        {m.watermarks_for_pack.map((w) => (
                          <div key={`${w.gun_idx}-${w.field}-${w.delta}`}>
                            • {w.gun_name || `gun #${w.gun_idx}`} — {w.field?.startsWith("h_") ? "H" : "V"}
                            {w.field && w.field.slice(2) !== "value" ? ` ${SIGHT_LABEL[w.field.slice(2)] || w.field.slice(2)} sight` : ""}
                            {" "}{w.delta > 0 ? "+" : ""}{w.delta}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {result.inferred?.length > 0 && (
                <details className="text-xs text-white/55">
                  <summary className="cursor-pointer text-white/65">Inferred deltas (debug)</summary>
                  <pre className="mt-2 bg-black/40 p-2 rounded border border-white/10 overflow-x-auto">{JSON.stringify(result.inferred, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
